import { CLAUDE_MODELS, EFFORT_LEVELS } from "@szyos/contracts";

/**
 * KI-Antworten des Planungsstudios an die Grenzen der Verträge anpassen, bevor sie geprüft werden.
 * Ein zu langer Text oder Schlüssel soll nicht einen ganzen, sonst guten Vorschlag verwerfen: Texte werden an einer
 * Wortgrenze gekürzt, Schlüssel bereinigt und Abhängigkeiten mitgeführt. Alles andere prüft danach der Vertrag.
 */

export const REPLY_LIMITS = {
  summary: 4000,
  question: 600,
  questions: 6,
  audience: 600,
  effortEstimate: 400,
  similarProject: 200,
  similarProjects: 5,
  key: 40,
  title: 200,
  criterion: 500,
  criteria: 20,
  specLong: 4000,
  specAudience: 2000,
  specLine: 500,
  specList: 50,
  modelReason: 400,
} as const;

/** Kürzt an einer Wortgrenze und hängt „…“ an; kurze Texte bleiben unverändert. */
export function shorten(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/** Plan-Schlüssel: Kleinbuchstaben, Ziffern, Bindestrich, höchstens 40 Zeichen, eindeutig. */
export function fitKey(raw: string, taken: ReadonlySet<string>): string {
  const base =
    raw
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, REPLY_LIMITS.key)
      .replace(/-+$/, "") || "aufgabe";
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const suffix = `-${n}`;
    const candidate = `${base.slice(0, REPLY_LIMITS.key - suffix.length).replace(/-+$/, "")}${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const fitText = (v: unknown, max: number) => (typeof v === "string" ? shorten(v, max) : v);
const fitList = (v: unknown, maxItem: number, maxCount: number) =>
  Array.isArray(v) ? v.slice(0, maxCount).map((x) => fitText(x, maxItem)) : v;

const MODELS = new Set<string>(CLAUDE_MODELS);
const EFFORTS = new Set<string>(EFFORT_LEVELS);

function fitPlanItems(items: unknown[]): unknown[] {
  const rename = new Map<string, string>();
  const taken = new Set<string>();
  for (const item of items) {
    if (!isObject(item) || typeof item.key !== "string") continue;
    const key = fitKey(item.key, taken);
    taken.add(key);
    rename.set(item.key, key);
  }
  return items.map((item) => {
    if (!isObject(item)) return item;
    // Modell und Aufwand sind Vorschläge: Unbekannte Werte fallen weg, statt den ganzen Plan zu verwerfen;
    // dann gilt für die Aufgabe die Regelberatung.
    const { model, effort, ...rest } = item;
    const lower = (v: unknown) => (typeof v === "string" ? v.trim().toLowerCase() : v);
    return {
      ...rest,
      ...(MODELS.has(lower(model) as string) ? { model: lower(model) } : {}),
      ...(EFFORTS.has(lower(effort) as string) ? { effort: lower(effort) } : {}),
      ...("modelReason" in item ? { modelReason: fitText(item.modelReason, REPLY_LIMITS.modelReason) } : {}),
      ...(typeof item.key === "string" ? { key: rename.get(item.key) ?? item.key } : {}),
      title: fitText(item.title, REPLY_LIMITS.title),
      acceptanceCriteria: fitList(item.acceptanceCriteria, REPLY_LIMITS.criterion, REPLY_LIMITS.criteria),
      dependsOn: Array.isArray(item.dependsOn)
        ? item.dependsOn.map((d) => (typeof d === "string" ? (rename.get(d) ?? d) : d))
        : item.dependsOn,
    };
  });
}

function fitSpecDraft(spec: Record<string, unknown>): Record<string, unknown> {
  return {
    ...spec,
    problem: fitText(spec.problem, REPLY_LIMITS.specLong),
    audience: fitText(spec.audience, REPLY_LIMITS.specAudience),
    designNotes: fitText(spec.designNotes, REPLY_LIMITS.specLong),
    mustHaves: fitList(spec.mustHaves, REPLY_LIMITS.specLine, REPLY_LIMITS.specList),
    nonGoals: fitList(spec.nonGoals, REPLY_LIMITS.specLine, REPLY_LIMITS.specList),
    acceptance: fitList(spec.acceptance, REPLY_LIMITS.specLine, REPLY_LIMITS.specList),
  };
}

/** Passt eine geparste KI-Antwort an die Grenzen an; unbekannte Formen bleiben unverändert für die Prüfung. */
export function fitStudioReply(data: unknown): unknown {
  if (!isObject(data)) return data;
  const out: Record<string, unknown> = {
    ...data,
    summary: fitText(data.summary, REPLY_LIMITS.summary),
    questions: fitList(data.questions, REPLY_LIMITS.question, REPLY_LIMITS.questions),
  };
  if ("audience" in data) out.audience = fitText(data.audience, REPLY_LIMITS.audience);
  if ("effortEstimate" in data)
    out.effortEstimate = fitText(data.effortEstimate, REPLY_LIMITS.effortEstimate);
  if ("similarProjects" in data) {
    out.similarProjects = fitList(
      data.similarProjects,
      REPLY_LIMITS.similarProject,
      REPLY_LIMITS.similarProjects,
    );
  }
  if (isObject(data.specDraft)) out.specDraft = fitSpecDraft(data.specDraft);
  if (isObject(data.planDraft) && Array.isArray(data.planDraft.items)) {
    out.planDraft = { ...data.planDraft, items: fitPlanItems(data.planDraft.items) };
  }
  if (out.questions === undefined) delete out.questions;
  return out;
}
